import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ConfigSelect, ConfigProblem } from './ConfigurationParts';
import { useTypeEditor } from './use-type-editor';
import type { Category, NoteType } from './api';
export function TypeCategories({
  setting,
  categories,
  refresh,
}: {
  readonly setting: NoteType;
  readonly categories: Category[];
  readonly refresh: () => void;
}) {
  const { t, i18n } = useTranslation('expenses');
  const available = categories.filter((category) => category.reportType === setting.noteType);
  const editor = useTypeEditor(setting, refresh, available);
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState('all');
  const name = (c: Category) => (i18n.language.startsWith('en') ? c.labelEn : c.labelIt);
  const shown = available
    .filter(
      (c) =>
        `${c.labelIt} ${c.labelEn}`.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
        (only === 'all' || editor.codes.includes(c.code) === (only === 'allowed')),
    )
    .sort((a, b) => name(a).localeCompare(name(b)));
  return (
    <section className="space-y-4 rounded-xl border border-border p-4">
      <h2 className="text-lg font-semibold">
        {t(`reports.noteType.${setting.noteType}`)} · {t('configuration.expenseTypes')}
      </h2>
      <p className="bg-muted rounded-lg p-3 text-sm">{t('configuration.typeImpact')}</p>
      <CategorySearch search={search} only={only} setSearch={setSearch} setOnly={setOnly} />
      <p role="status" className="text-muted-foreground text-sm">
        {t('configuration.categoryCount', { count: editor.codes.length, total: available.length })}
      </p>
      <fieldset disabled={editor.busy} className="grid gap-x-5 sm:grid-cols-2">
        <legend className="sr-only">{t('configuration.expenseTypes')}</legend>
        {shown.map((category) => (
          <CategoryChoice
            key={category.code}
            category={category}
            name={name(category)}
            checked={editor.codes.includes(category.code)}
            toggle={editor.toggle}
          />
        ))}
      </fieldset>
      {shown.length === 0 ? (
        <div className="space-y-2">
          <p>{t('configuration.noMatches')}</p>
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => {
              setSearch('');
              setOnly('all');
            }}
          >
            {t('configuration.reset')}
          </Button>
        </div>
      ) : null}
      <TypeSave editor={editor} setting={setting} categories={categories} refresh={refresh} />
    </section>
  );
}
function TypeSave({
  editor: e,
  setting,
  categories,
  refresh,
}: {
  readonly editor: ReturnType<typeof useTypeEditor>;
  readonly setting: NoteType;
  readonly categories: Category[];
  readonly refresh: () => void;
}) {
  const { t, i18n } = useTranslation('expenses');
  const stale = e.revision !== setting.revision;
  const blocked = e.error === 'CONFIGURATION_CHANGED';
  const latest = categories
    .filter((c) => setting.categoryCodes.includes(c.code))
    .map((c) => (i18n.language.startsWith('en') ? c.labelEn : c.labelIt))
    .join(', ');
  return (
    <>
      {e.codes.length === 0 ? (
        <p role="alert" className="text-destructive text-sm">
          {t('configuration.categoryHint')}
        </p>
      ) : null}
      {e.error ? <ConfigProblem code={e.error} retry={refresh} /> : null}
      {stale || blocked ? (
        <div className="space-y-2 text-sm">
          <p>{t('configuration.typeConflict')}</p>
          {stale ? <p>{t('configuration.latestTypes', { types: latest })}</p> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          className="min-h-11"
          disabled={
            !setting.can.edit || !e.changed || e.busy || e.codes.length === 0 || stale || blocked
          }
          onClick={() => void e.save()}
        >
          {t(e.busy ? 'configuration.saving' : 'configuration.save')}
        </Button>
        {stale ? (
          <Button variant="outline" className="min-h-11" onClick={e.useLatest}>
            {t('configuration.keepEdits')}
          </Button>
        ) : null}
        {!setting.can.edit ? (
          <p className="text-muted-foreground text-sm">{t('configuration.typeEditReason')}</p>
        ) : null}
        {!e.changed ? (
          <p className="text-muted-foreground text-sm">{t('configuration.unchanged')}</p>
        ) : null}
      </div>
    </>
  );
}

function CategoryChoice({
  category,
  name,
  checked,
  toggle,
}: {
  readonly category: Category;
  readonly name: string;
  readonly checked: boolean;
  readonly toggle: (code: string) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <label className="flex min-h-11 items-center gap-3 py-2 text-sm">
      <Checkbox
        checked={checked}
        disabled={category.required}
        onChange={() => toggle(category.code)}
      />
      <span>
        {name}
        {category.required ? (
          <small className="text-muted-foreground block">{t('configuration.pinnedCategory')}</small>
        ) : null}
      </span>
    </label>
  );
}

function CategorySearch({
  search,
  only,
  setSearch,
  setOnly,
}: {
  readonly search: string;
  readonly only: string;
  readonly setSearch: (value: string) => void;
  readonly setOnly: (value: string) => void;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div className="flex flex-wrap gap-3">
      <label className="min-w-0 flex-1 text-sm">
        <span className="sr-only">{t('configuration.categorySearch')}</span>
        <Input
          className="min-h-11"
          type="search"
          placeholder={t('configuration.categorySearch')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>
      <ConfigSelect
        label={t('configuration.show')}
        value={only}
        options={(['all', 'allowed', 'excluded'] as const).map((value) => ({
          value,
          label: t(`configuration.${value}`),
        }))}
        onChange={setOnly}
      />
    </div>
  );
}
